<template>
  <div class="admin-notification px-2">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0 text-white" style="font-size: 1.25rem;">站内通知管理</h4>
    </div>

    <b-tabs content-class="mt-3" active-nav-item-class="font-weight-bold text-primary" class="admin-custom-tabs">
      <!-- Tab 1: 发送通知 -->
      <b-tab active>
        <template #title>
          <span>📨 发送站内通知</span>
        </template>

        <div class="row">
          <!-- 左侧：表单输入 -->
          <div class="col-lg-7 col-12 mb-4">
            <div class="card bg-dark-soft border-0 shadow-sm p-3">
              <h5 class="text-white mb-3" style="font-size: 1rem; border-left: 3px solid #2563eb; padding-left: 8px;">
                编写通知内容
              </h5>

              <!-- 发送对象 -->
              <b-form-group label="发送对象" label-class="text-white-50 small font-weight-bold">
                <b-form-radio-group
                  v-model="form.targetType"
                  :options="targetOptions"
                  button-variant="outline-primary"
                  size="sm"
                  buttons
                  class="w-100 d-flex"
                ></b-form-radio-group>
              </b-form-group>

              <!-- 指定用户ID输入框 -->
              <b-form-group
                v-if="form.targetType === 'single'"
                label="接收用户 ID (user_id)"
                label-class="text-white-50 small font-weight-bold"
                description="请输入接收通知的数字用户 ID"
              >
                <b-form-input
                  v-model.number="form.userId"
                  type="number"
                  placeholder="例如：1001"
                  size="sm"
                  class="bg-dark text-white border-secondary"
                ></b-form-input>
              </b-form-group>

              <!-- 通知类型 -->
              <b-form-group label="通知类型" label-class="text-white-50 small font-weight-bold">
                <b-form-radio-group
                  v-model="form.type"
                  :options="typeOptions"
                  button-variant="outline-info"
                  size="sm"
                  buttons
                ></b-form-radio-group>
              </b-form-group>

              <!-- 标题 -->
              <b-form-group label="通知标题" label-class="text-white-50 small font-weight-bold">
                <b-form-input
                  v-model.trim="form.title"
                  placeholder="请输入通知标题 (最多120字)"
                  maxlength="120"
                  class="bg-dark text-white border-secondary"
                ></b-form-input>
              </b-form-group>

              <!-- 正文内容 -->
              <b-form-group label="通知正文内容" label-class="text-white-50 small font-weight-bold">
                <b-form-textarea
                  v-model="form.content"
                  placeholder="请输入通知正文内容，支持换行..."
                  rows="5"
                  max-rows="10"
                  class="bg-dark text-white border-secondary"
                ></b-form-textarea>
              </b-form-group>

              <!-- 跳转链接 (可选) -->
              <b-form-group
                label="页面跳转链接 (可选)"
                label-class="text-white-50 small font-weight-bold"
                description="例如：/user/subscription 或 /wonder 等系统内路径"
              >
                <b-form-input
                  v-model.trim="form.link"
                  placeholder="如: /user/subscription"
                  class="bg-dark text-white border-secondary"
                ></b-form-input>
              </b-form-group>

              <!-- 操作按钮 -->
              <div class="d-flex justify-content-end mt-3" style="gap: 12px;">
                <b-button variant="secondary" size="sm" @click="resetForm" :disabled="sending">
                  清空重置
                </b-button>
                <b-button variant="primary" size="sm" @click="confirmSend" :disabled="sending">
                  <b-spinner v-if="sending" small type="grow" class="mr-1"></b-spinner>
                  {{ sending ? '正在发送...' : '确认发送通知' }}
                </b-button>
              </div>
            </div>
          </div>

          <!-- 右侧：实时效果预览 -->
          <div class="col-lg-5 col-12 mb-4">
            <div class="card bg-dark-soft border-0 shadow-sm p-3">
              <h5 class="text-white mb-3" style="font-size: 1rem; border-left: 3px solid #10b981; padding-left: 8px;">
                用户端实时显示预览
              </h5>

              <div class="preview-box p-3 rounded" style="background: var(--bg-card, #1e293b); border: 1px solid var(--border-color, #334155);">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span :class="badgeClass(form.type)" class="badge font-weight-normal px-2 py-1">
                    {{ typeLabel(form.type) }}
                  </span>
                  <small class="text-muted">刚刚</small>
                </div>

                <div class="font-weight-bold text-white mb-2" style="font-size: 1rem;">
                  {{ form.title || '（标题预览）' }}
                </div>

                <div class="text-white-50 small mb-3" style="white-space: pre-wrap; line-height: 1.6; word-break: break-all;">
                  {{ form.content || '（正文预览：这里将显示通知的详细内容...）' }}
                </div>

                <div v-if="form.link" class="pt-2 border-top border-secondary">
                  <span class="text-primary small">
                    🔗 查看详情 ({{ form.link }})
                  </span>
                </div>
              </div>

              <div class="mt-3 text-muted small">
                <i class="mr-1">💡</i>
                <span v-if="form.targetType === 'all_vip'">
                  当前模式为<strong>全量群发</strong>，点击发送后将批量送达所有处于有效期内的 VIP 会员站内信箱。
                </span>
                <span v-else>
                  当前模式为<strong>单用户定向发送</strong>，通知仅会推送给指定的 User ID。
                </span>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- Tab 2: 历史通知记录 -->
      <b-tab @click="fetchHistory">
        <template #title>
          <span>📋 历史发送记录</span>
        </template>

        <div class="card bg-dark-soft border-0 shadow-sm">
          <!-- 顶部筛选栏 -->
          <div class="card-header bg-transparent border-secondary p-3">
            <div class="row align-items-center">
              <div class="col-md-3 col-sm-6 mb-2 mb-md-0">
                <b-form-input
                  v-model.number="historyFilter.userId"
                  type="number"
                  size="sm"
                  placeholder="按接收用户 ID 筛选"
                  class="bg-dark text-white border-secondary"
                  @keyup.enter="fetchHistory"
                ></b-form-input>
              </div>
              <div class="col-md-3 col-sm-6 mb-2 mb-md-0">
                <b-form-select
                  v-model="historyFilter.type"
                  :options="filterTypeOptions"
                  size="sm"
                  class="bg-dark text-white border-secondary"
                  @change="fetchHistory"
                ></b-form-select>
              </div>
              <div class="col-md-6 col-sm-12 text-right">
                <b-button variant="outline-primary" size="sm" @click="fetchHistory" :disabled="historyLoading" class="mr-2">
                  刷新列表
                </b-button>
                <b-button variant="outline-secondary" size="sm" @click="resetHistoryFilter">
                  重置筛选
                </b-button>
              </div>
            </div>
          </div>

          <div class="card-body p-0">
            <div v-if="historyLoading" class="text-center py-5">
              <b-spinner variant="primary"></b-spinner>
              <div class="text-muted small mt-2">加载历史记录中...</div>
            </div>

            <div v-else-if="!historyItems.length" class="text-center py-5 text-muted">
              暂无站内通知记录
            </div>

            <template v-else>
              <!-- PC 端表格视图 -->
              <div class="d-none d-lg-block">
                <div class="table-responsive">
                  <table class="table table-dark table-hover table-sm mb-0">
                    <thead>
                      <tr>
                        <th style="width: 70px;">ID</th>
                        <th style="width: 90px;">用户 ID</th>
                        <th style="width: 100px;">类型</th>
                        <th style="width: 180px;">标题</th>
                        <th>通知正文</th>
                        <th style="width: 130px;">链接</th>
                        <th style="width: 80px;">状态</th>
                        <th style="width: 150px;">发送时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in historyItems" :key="item.id">
                        <td>{{ item.id }}</td>
                        <td>
                          <span class="badge badge-secondary">UID: {{ item.user_id }}</span>
                        </td>
                        <td>
                          <span :class="badgeClass(item.type)" class="badge">
                            {{ typeLabel(item.type) }}
                          </span>
                        </td>
                        <td class="font-weight-bold text-white">{{ item.title }}</td>
                        <td>
                          <div class="text-white-50 text-truncate" style="max-width: 360px;" :title="item.content">
                            {{ item.content }}
                          </div>
                        </td>
                        <td>
                          <span v-if="item.link" class="text-primary small text-truncate d-inline-block" style="max-width: 120px;" :title="item.link">
                            {{ item.link }}
                          </span>
                          <span v-else class="text-muted small">-</span>
                        </td>
                        <td>
                          <span v-if="item.is_read" class="badge badge-success">已读</span>
                          <span v-else class="badge badge-warning">未读</span>
                        </td>
                        <td class="text-muted small">{{ item.created_at }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- 移动端卡片视图 -->
              <div class="d-block d-lg-none p-2">
                <div
                  v-for="item in historyItems"
                  :key="item.id"
                  class="card mb-2 p-2"
                  style="background: #1e293b; border: 1px solid #334155;"
                >
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span :class="badgeClass(item.type)" class="badge">
                      {{ typeLabel(item.type) }}
                    </span>
                    <span class="badge badge-secondary">UID: {{ item.user_id }}</span>
                    <small class="text-muted">{{ item.created_at }}</small>
                  </div>
                  <div class="font-weight-bold text-white small mb-1">{{ item.title }}</div>
                  <div class="text-white-50 small mb-1" style="font-size: 0.8rem;">{{ item.content }}</div>
                  <div class="d-flex justify-content-between align-items-center mt-1 pt-1 border-top border-secondary">
                    <span v-if="item.link" class="text-primary small">{{ item.link }}</span>
                    <span v-else class="text-muted small">无链接</span>
                    <span v-if="item.is_read" class="badge badge-success">已读</span>
                    <span v-else class="badge badge-warning">未读</span>
                  </div>
                </div>
              </div>

              <!-- 分页控件 -->
              <div class="d-flex justify-content-between align-items-center p-3 border-top border-secondary flex-wrap">
                <div class="text-muted small mb-2 mb-sm-0">
                  共 {{ historyTotal }} 条记录，第 {{ historyPage }} / {{ Math.ceil(historyTotal / historyPageSize) || 1 }} 页
                </div>
                <b-pagination
                  v-model="historyPage"
                  :total-rows="historyTotal"
                  :per-page="historyPageSize"
                  size="sm"
                  class="mb-0"
                  @change="handlePageChange"
                ></b-pagination>
              </div>
            </template>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'

export default {
  name: 'AdminNotification',
  data() {
    return {
      sending: false,
      form: {
        targetType: 'all_vip',
        userId: null,
        type: 'system',
        title: '',
        content: '',
        link: ''
      },
      targetOptions: [
        { text: '📢 全量有效 VIP 会员 (系统群发)', value: 'all_vip' },
        { text: '👤 指定单个用户 ID', value: 'single' }
      ],
      typeOptions: [
        { text: '系统通知', value: 'system' },
        { text: 'VIP 权益', value: 'vip' },
        { text: '活动公告', value: 'activity' }
      ],
      // 历史记录
      historyLoading: false,
      historyItems: [],
      historyTotal: 0,
      historyPage: 1,
      historyPageSize: 20,
      historyFilter: {
        userId: null,
        type: null
      },
      filterTypeOptions: [
        { text: '全部通知类型', value: null },
        { text: '系统通知 (system)', value: 'system' },
        { text: 'VIP 权益 (vip)', value: 'vip' },
        { text: '活动公告 (activity)', value: 'activity' }
      ]
    }
  },
  methods: {
    badgeClass(type) {
      if (type === 'vip') return 'badge-warning text-dark'
      if (type === 'activity') return 'badge-success'
      return 'badge-primary'
    },
    typeLabel(type) {
      if (type === 'vip') return 'VIP 权益'
      if (type === 'activity') return '活动公告'
      return '系统通知'
    },
    resetForm() {
      this.form.targetType = 'all_vip'
      this.form.userId = null
      this.form.type = 'system'
      this.form.title = ''
      this.form.content = ''
      this.form.link = ''
    },
    confirmSend() {
      if (!this.form.title) {
        this.showToast('请输入通知标题', 'warning')
        return
      }
      if (!this.form.content) {
        this.showToast('请输入通知正文内容', 'warning')
        return
      }
      if (this.form.targetType === 'single' && !this.form.userId) {
        this.showToast('指定单个用户时，必须输入用户 ID', 'warning')
        return
      }

      const targetDesc = this.form.targetType === 'all_vip'
        ? '所有有效 VIP 会员'
        : `用户 ID 为 ${this.form.userId} 的用户`

      this.$bvModal.msgBoxConfirm(`确定要将该站内通知发送给【${targetDesc}】吗？`, {
        title: '发送站内通知确认',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: '立即发送',
        cancelTitle: '取消',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(confirmed => {
        if (confirmed) {
          this.executeSend()
        }
      })
    },
    executeSend() {
      this.sending = true
      const payload = {
        title: this.form.title,
        content: this.form.content,
        type: this.form.type,
        link: this.form.link || null,
        to_all_vip: this.form.targetType === 'all_vip',
        user_id: this.form.targetType === 'single' ? Number(this.form.userId) : null
      }

      request_zzquant({
        url: '/v3/notifications/admin/send',
        method: 'post',
        data: payload
      }).then(res => {
        if (res.data && res.data.code === 20000) {
          const d = res.data.data
          const msg = this.form.targetType === 'all_vip'
            ? `发送成功！已成功向 ${d.affected_users || d.sent_count || 0} 位 VIP 会员发送通知。`
            : `发送成功！已成功向用户 ID ${this.form.userId} 发送通知。`
          this.showToast(msg, 'success')
          this.resetForm()
        } else {
          this.showToast((res.data && res.data.message) || '发送失败，请重试', 'danger')
        }
      }).catch(err => {
        this.showToast(`发送出错: ${err.message || '网络异常'}`, 'danger')
      }).finally(() => {
        this.sending = false
      })
    },
    fetchHistory() {
      this.historyLoading = true
      const params = {
        page: this.historyPage,
        page_size: this.historyPageSize
      }
      if (this.historyFilter.userId) {
        params.user_id = this.historyFilter.userId
      }
      if (this.historyFilter.type) {
        params.type = this.historyFilter.type
      }

      request_zzquant({
        url: '/v3/notifications/admin/list',
        method: 'get',
        params: params
      }).then(res => {
        if (res.data && res.data.code === 20000) {
          this.historyItems = res.data.data.items || []
          this.historyTotal = res.data.data.total || 0
        }
      }).catch(err => {
        this.showToast(`加载历史记录失败: ${err.message}`, 'danger')
      }).finally(() => {
        this.historyLoading = false
      })
    },
    handlePageChange(page) {
      this.historyPage = page
      this.fetchHistory()
    },
    resetHistoryFilter() {
      this.historyFilter.userId = null
      this.historyFilter.type = null
      this.historyPage = 1
      this.fetchHistory()
    },
    showToast(msg, variant = 'info') {
      this.$bvToast.toast(msg, {
        title: '系统提示',
        variant: variant,
        solid: true,
        autoHideDelay: 3500
      })
    }
  }
}
</script>

<style scoped>
.bg-dark-soft {
  background: var(--bg-card, #1e293b) !important;
}

.admin-custom-tabs /deep/ .nav-tabs {
  border-bottom: 1px solid var(--border-color, #334155);
}

.admin-custom-tabs /deep/ .nav-tabs .nav-link {
  color: #94a3b8;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  padding: 10px 18px;
}

.admin-custom-tabs /deep/ .nav-tabs .nav-link.active {
  color: #3b82f6 !important;
  border-bottom-color: #3b82f6;
  background: transparent;
}

.admin-custom-tabs /deep/ .nav-tabs .nav-link:hover {
  color: #e2e8f0;
}
</style>
